/**
 * Report Templates API 服務
 * 對應後端 /api/report-templates
 */

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const API_BASE = '/api/report-templates';

export const reportTemplatesApi = {
  /** 列出公司所有範本 */
  list: async () => {
    const res = await fetch(API_BASE, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** 取得範本詳情（含 Placeholder 清單） */
  get: async (id) => {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** 上傳新範本 */
  upload: async (file, name, description, category) => {
    const form = new FormData();
    form.append('file', file);
    if (name) form.append('name', name);
    if (description) form.append('description', description);
    if (category) form.append('category', category);

    const res = await fetch(API_BASE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: form,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** 更新名稱/描述/分類 */
  updateMeta: async (id, name, description, category) => {
    const res = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, description, category }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** 儲存 PV/EFORM/SYS 變量綁定 */
  updateBindings: async (id, bindings) => {
    const res = await fetch(`${API_BASE}/${id}/bindings`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(bindings),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** 下載原始範本檔案
   * @param {string} id - 範本 ID
   * @param {string} [fallbackFilename] - 當後端未回傳 Content-Disposition 時使用的備用檔名（含副檔名）
   */
  download: async (id, fallbackFilename) => {
    const res = await fetch(`${API_BASE}/${id}/download`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(await res.text());
    const contentDisposition = res.headers.get('Content-Disposition') || '';
    let filename = fallbackFilename || 'template';
    const rfcMatch = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
    if (rfcMatch) {
      try { filename = decodeURIComponent(rfcMatch[1]); } catch { filename = rfcMatch[1]; }
    } else {
      const legacyMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (legacyMatch) filename = legacyMatch[1].replace(/['"]/g, '');
    }
    const blob = await res.blob();
    return { blob, filename };
  },

  /** 軟刪除範本 */
  delete: async (id) => {
    const res = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** 批量下載（ZIP） */
  batchDownload: async (ids) => {
    const res = await fetch(`${API_BASE}/batch-download`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error(await res.text());
    const contentDisposition = res.headers.get('Content-Disposition') || '';
    let filename = `templates_${new Date().toISOString().slice(0, 10)}.zip`;
    const rfcMatch = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
    if (rfcMatch) {
      try { filename = decodeURIComponent(rfcMatch[1]); } catch { filename = rfcMatch[1]; }
    } else {
      const legacyMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (legacyMatch) filename = legacyMatch[1].replace(/['"]/g, '');
    }
    const blob = await res.blob();
    return { blob, filename };
  },

  /** 批量軟刪除 */
  batchDelete: async (ids) => {
    const res = await fetch(`${API_BASE}/batch`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** AI 推薦照片配對 */
  suggestMapping: async (id, photos, contextKeywords) => {
    const res = await fetch(`${API_BASE}/${id}/suggest-mapping`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ photos, contextKeywords }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** 生成報表並下載 */
  generate: async (id, { photoMapping, pvValues, eformValues, workflowName, executionId }) => {
    const res = await fetch(`${API_BASE}/${id}/generate`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoMapping, pvValues, eformValues, workflowName, executionId }),
    });

    if (!res.ok) {
      // Read body as text first (stream can only be read once), then try JSON parse
      let rawText = '';
      try { rawText = await res.text(); } catch { rawText = ''; }
      let detail = rawText;
      try {
        const json = JSON.parse(rawText);
        detail = json.detail || json.error || json.title || JSON.stringify(json);
      } catch { /* not JSON – use rawText as-is */ }
      throw new Error(detail || `HTTP ${res.status}`);
    }

    const contentDisposition = res.headers.get('Content-Disposition') || '';
    // Prefer RFC 5987 filename* (supports Unicode), fall back to filename=
    let filename = 'report';
    const rfcMatch = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
    if (rfcMatch) {
      try { filename = decodeURIComponent(rfcMatch[1]); } catch { filename = rfcMatch[1]; }
    } else {
      const legacyMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (legacyMatch) filename = legacyMatch[1].replace(/['"]/g, '');
    }

    const blob = await res.blob();
    if (blob.size === 0) {
      throw new Error('伺服器回傳空檔案，請查看後台 Log');
    }
    return { blob, filename };
  },
};

export default reportTemplatesApi;
